import { useMemo } from 'react';
import { color } from '@stockbit/ui';
import numeral from 'numeral';
import numberUtils, { parseCountAbbrv2 } from 'utils/number';
import SkeletonWrapper from 'global/Securities/components/SkeletonWrapper';
import { OrderbookCompaniesResponse } from 'global/Securities/types/api/orderbook';
import { getStockPriceMovement } from './utils';

import {
  SecureTable,
  SecureTableContainer,
  SecureTableData,
  SecureTableRow,
} from './TableSecure.styled';
import { stockPriceMovementColor } from './constants';
import TableSecureSkeleton from './TableSecureSkeleton';

type TableSecureData = Partial<
  Pick<
    OrderbookCompaniesResponse,
    | 'previous'
    | 'open'
    | 'volume'
    | 'change'
    | 'high'
    | 'value'
    | 'percentage_change'
    | 'low'
    | 'average'
    | 'frequency'
    | 'fbuy'
    | 'fsell'
    | 'is_foreignbs_exist'
    | 'iepiev'
    | 'ara'
    | 'arb'
  >
>;

interface TableSecureProps {
  data?: TableSecureData;
  columnsWidth?: number[];
  loading?: boolean;
  isStock?: boolean;
}

const defaultColumnsWidth = [24, 53.3, 30, 47.33, 21, 56.3];
const TableSecure = ({
  data = {},
  columnsWidth = defaultColumnsWidth,
  loading,
  isStock = false,
}: TableSecureProps) => {
  const formatNumber = (value: number, format = '0,0') =>
    numeral(value).format(format);

  const getTextColor = (currentPrice: number, previousPrice = data.previous) =>
    stockPriceMovementColor[getStockPriceMovement(currentPrice, previousPrice)];

  const [Iep, Iev, showIepIev] = useMemo(() => {
    if (!data.iepiev?.iep && !data.iepiev?.iev) return [];

    const iepRaw = data.iepiev.iep.raw;
    const ievRaw = data.iepiev.iev.raw;

    const iep = formatNumber(iepRaw);
    const iev = data.iepiev.iev.raw
      ? parseCountAbbrv2(ievRaw, 1, ',', true)
      : 0;

    const timeleft = data.iepiev.time_left_seconds;
    const showiepiev =
      iep && iev && (timeleft !== undefined ? timeleft > 0 : true);

    return [iep, iev, showiepiev];
  }, [data.iepiev]);

  const fourthRowExist = useMemo(
    () => showIepIev || data.is_foreignbs_exist,
    [showIepIev, data.is_foreignbs_exist],
  );

  const lot = useMemo(
    () =>
      data.volume
        ? numberUtils.parseCountAbbrv3(numberUtils.parseLot(data.volume), 1, 4)
        : 'NA',
    [data.volume],
  );

  if (!loading && !data) return <></>;
  return (
    <SkeletonWrapper skeleton={<TableSecureSkeleton />} loading={loading}>
      <SecureTableContainer>
        <SecureTable>
          {isStock ? (
            <tbody>
              <SecureTableRow minWidth={columnsWidth}>
                <SecureTableData>Open</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.open)}
                  data-cy="open-content"
                >
                  {formatNumber(data.open)}
                </SecureTableData>

                <SecureTableData>Prev</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.previous)}
                  data-cy="previous-content"
                >
                  {formatNumber(data.previous)}
                </SecureTableData>

                <SecureTableData>Lot</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.change, 0)}
                  data-cy="volume-content"
                >
                  {lot}
                </SecureTableData>
              </SecureTableRow>
              <SecureTableRow minWidth={columnsWidth}>
                <SecureTableData>High</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.high)}
                  data-cy="high-content"
                >
                  {formatNumber(data.high)}
                </SecureTableData>

                <SecureTableData>ARA</SecureTableData>
                <SecureTableData align="right" data-cy="ara-content">
                  {data?.ara?.visible ? data?.ara?.value : '-'}
                </SecureTableData>

                <SecureTableData>Val</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.change, 0)}
                  data-cy="value-content"
                >
                  {numberUtils.numberFormatter({ num: data.value, fixed: 1 })}
                </SecureTableData>
              </SecureTableRow>
              <SecureTableRow minWidth={columnsWidth}>
                <SecureTableData>Low</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.low)}
                  data-cy="low-content"
                >
                  {formatNumber(data.low)}
                </SecureTableData>

                <SecureTableData>ARB</SecureTableData>
                <SecureTableData align="right" data-cy="arb-content">
                  {data?.arb?.visible ? data?.arb?.value : '-'}
                </SecureTableData>

                <SecureTableData>Avg</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.average)}
                  data-cy="average-content"
                >
                  {formatNumber(data.average)}
                </SecureTableData>
              </SecureTableRow>

              {fourthRowExist && (
                <tr>
                  {showIepIev ? (
                    <>
                      <SecureTableData>IEP</SecureTableData>
                      <SecureTableData
                        align="right"
                        data-cy="iep-content"
                        color={getTextColor(data.iepiev?.iep?.raw)}
                      >
                        {Iep}
                      </SecureTableData>

                      <SecureTableData>IEV</SecureTableData>
                      <SecureTableData align="right" data-cy="iev-content">
                        {numberUtils.kNumberFormatter(Iev, 0, true)}
                      </SecureTableData>
                    </>
                  ) : (
                    <>
                      <SecureTableData>F Buy</SecureTableData>
                      <SecureTableData align="right" data-cy="fbuy-content" color={color.primary}>
                        {numberUtils.numberFormatter({
                          num: data.fbuy,
                          fixed: 1,
                        })}
                      </SecureTableData>

                      <SecureTableData>F Sell</SecureTableData>
                      <SecureTableData align="right" data-cy="fsell-content" color={color.sunsetOrange}>
                        {numberUtils.numberFormatter({
                          num: data.fsell,
                          fixed: 1,
                        })}
                      </SecureTableData>
                    </>
                  )}

                  <SecureTableData>Freq</SecureTableData>
                  <SecureTableData align="right" data-cy="frequency-content">
                    {formatNumber(data.frequency)}
                  </SecureTableData>
                </tr>
              )}
            </tbody>
          ) : (
            <tbody>
              <SecureTableRow minWidth={columnsWidth}>
                <SecureTableData>Prev</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.previous)}
                  data-cy="previous-content"
                >
                  {formatNumber(data.previous)}
                </SecureTableData>

                <SecureTableData>Open</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.open)}
                  data-cy="open-content"
                >
                  {formatNumber(data.open)}
                </SecureTableData>

                <SecureTableData>Lot</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.change, 0)}
                  data-cy="volume-content"
                >
                  {lot}
                </SecureTableData>
              </SecureTableRow>
              <SecureTableRow minWidth={columnsWidth}>
                <SecureTableData>Chg</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.change, 0)}
                  data-cy="change-content"
                >
                  {data.change > 0 && '+'}
                  {formatNumber(data.change)}
                </SecureTableData>

                <SecureTableData>High</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.high)}
                  data-cy="high-content"
                >
                  {formatNumber(data.high)}
                </SecureTableData>

                <SecureTableData>Val</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.change, 0)}
                  data-cy="value-content"
                >
                  {numberUtils.numberFormatter({ num: data.value, fixed: 1 })}
                </SecureTableData>
              </SecureTableRow>
              <SecureTableRow minWidth={columnsWidth}>
                <SecureTableData>%</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.percentage_change, 0)}
                  data-cy="percentage_change-content"
                >
                  {formatNumber(data.percentage_change, '0.00')}%
                </SecureTableData>

                <SecureTableData>Low</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.low)}
                  data-cy="low-content"
                >
                  {formatNumber(data.low)}
                </SecureTableData>

                <SecureTableData>Avg</SecureTableData>
                <SecureTableData
                  align="right"
                  color={getTextColor(data.average)}
                  data-cy="average-content"
                >
                  {formatNumber(data.average)}
                </SecureTableData>
              </SecureTableRow>

              {fourthRowExist && (
                <tr>
                  <SecureTableData>Freq</SecureTableData>
                  <SecureTableData align="right" data-cy="frequency-content">
                    {formatNumber(data.frequency)}
                  </SecureTableData>

                  {showIepIev ? (
                    <>
                      <SecureTableData>IEP</SecureTableData>
                      <SecureTableData
                        align="right"
                        data-cy="iep-content"
                        color={getTextColor(data.iepiev?.iep?.raw)}
                      >
                        {Iep}
                      </SecureTableData>

                      <SecureTableData>IEV</SecureTableData>
                      <SecureTableData align="right" data-cy="iev-content">
                        {numberUtils.kNumberFormatter(Iev, 0, true)}
                      </SecureTableData>
                    </>
                  ) : (
                    <>
                      <SecureTableData>F Buy</SecureTableData>
                      <SecureTableData align="right" data-cy="fbuy-content" color={color.primary}>
                        {numberUtils.numberFormatter({
                          num: data.fbuy,
                          fixed: 1,
                        })}
                      </SecureTableData>

                      <SecureTableData>F Sell</SecureTableData>
                      <SecureTableData align="right" data-cy="fsell-content" color={color.sunsetOrange}>
                        {numberUtils.numberFormatter({
                          num: data.fsell,
                          fixed: 1,
                        })}
                      </SecureTableData>
                    </>
                  )}
                </tr>
              )}
            </tbody>
          )}
        </SecureTable>
      </SecureTableContainer>
    </SkeletonWrapper>
  );
};

export default TableSecure;
